import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration } from 'chart.js';
import moment from 'moment';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { resolve } from 'chart.js/helpers';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';

@Component({
    selector: 'cv-qm-evaluator-performance-trend',
    templateUrl: './qm-evaluator-performance-trend.component.html',
    styleUrl: './qm-evaluator-performance-trend.component.scss'
})
export class QmEvaluatorPerformanceTrendComponent {
    @ViewChild(BaseChartDirective) chartObject: BaseChartDirective;
    @Output() chartRef: EventEmitter<any> = new EventEmitter<any>;
    public chartConfig: ChartConfiguration;
    public _chartData = {};
    public noAgentsSelected = false;
    public previousRange: { yMin: number; yMax: number; } = null;
    public rightPadding = 20;
    public bottomPadding = 11.37;
    public leftPadding = 2.4;
    isCXOne = false;
    @Input() set chartData(data: any) {
        if (Object.keys(data).length > 0) {
            this._chartData = new Map(Object.entries(data));
            this.updateChartData();
            this.noAgentsSelected = false;
        } else {
            this._chartData = {};
            this.noAgentsSelected = true;
        }
    };
    @Input() set fetchChartObj(data: any) {
        this.chartRef.emit(this.chartObject)
    }

    get chartData(): any {
        return this._chartData;
    }

    constructor(
        private config: CvConfigurationService,
        public cvLocalizationService: CVLocalizationService
    ) {
        this.isCXOne = this.config.isCxOne;
    }

    async ngOnInit(): Promise<void> {
        const localizationPromises = [
            this.cvLocalizationService.GetSection('Evaluator_Performance'),
            this.cvLocalizationService.GetSection("Error_Module"),
            this.cvLocalizationService.GetSection("Common"),
        ];
        await Promise.all(localizationPromises);
    }

    getChartConfig(): ChartConfiguration {
        const completedEvaluationsOverTime = this.cvLocalizationService.getCachedText('Evaluator_Performance', 'Completed_Evaluations_Over_Time', 'Completed Evaluations Over Time');
        return {
            type: "line",
            data: {
                datasets: [],
                labels: []
            },
            plugins: [{
                id: 'custom_canvas_background_color',
                beforeDraw: (chart) => {
                    const ctx = chart.canvas.getContext('2d');
                    ctx.save();
                    ctx.globalCompositeOperation = 'destination-over';
                    ctx.fillStyle = '#FFFFFF';
                    ctx.fillRect(0, 0, chart.canvas.width, chart.canvas.height);
                    ctx.restore();
                }
            }, {
                id: 'drawVerticalLine'
            }],
            options: {
                onHover: this.onHoverChart.bind(this),
                datasets: {
                    line: {
                        spanGaps: true,
                    },
                },
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    emptyChart: {
                        disableCxone: this.config.isCxOne,
                    },
                    legend: {
                        display: true,
                        position: "top",
                        align: 'center',
                        labels: {
                            boxWidth: 12,
                            boxHeight: 12,
                            color: 'black',
                            font: {
                                family: "'Lato-Regular', 'Helvetica Neue', 'Helvetica', 'Arial', 'sans-serif'",
                                size: 13,
                            },
                            generateLabels: (chart) => {
                                const dataset = chart.data.datasets[0];
                                const meta = chart.getDatasetMeta(0);
                                const arc = meta.data[0];
                                const custom = arc && arc.options || {};
                                const arcOpts = chart.options.elements.arc;
                                const toggle = chart.getDataVisibility(0);
                                const bw = custom.borderWidth ? custom.borderWidth :
                                    resolve([dataset.borderWidth, arcOpts.borderWidth], chart.ctx, 0);
                                return [{
                                    text: completedEvaluationsOverTime,
                                    fillStyle: dataset.borderColor,
                                    strokeStyle: dataset.borderColor,
                                    lineWidth: bw,
                                    hidden: !toggle,
                                    index: 0
                                }];
                            },

                        },
                    },
                    tooltip: {
                        enabled: false,
                        external: (context) => {
                            this.tooltipExternalHandle(context);
                        }
                    }
                },
                scales: {
                    x: {
                        distribution: 'series',
                        scaleLabel: {
                            display: false,
                        },
                        grid: {
                            display: true,
                            color: 'rgba(232, 232, 232, 1)'
                        },
                        ticks: {
                            autoSkip: true,
                            source: 'labels',
                            maxTicksLimit: 5,
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: this.cvLocalizationService.getCachedText('Evaluator_Performance', 'Completed_Evaluations_Y_Axis_Title', '# of Evaluations'),
                            font: {
                                family: 'Open Sans, sans-serif',
                                size: 12,
                                weight: '400',
                            },
                        },
                        grid: {
                            display: true,
                            color: 'rgba(232, 232, 232, 1)'
                        },
                        border: {
                            display: true,
                            dash: [4, 4],
                        },
                        suggestedMax: 10,
                        suggestedMin: 0,
                    }
                },
            },
        } as ChartConfiguration;
    }

    onMouseOut(event) {
        this.previousRange = null;
        this.chartObject.chart.update();
    }

    updateChartData() {
        if (!this.chartData)
            return;

        this.chartConfig = this.getChartConfig();
        const data: any[] = [];
        const labels: string[] = [];
        this.chartData.forEach((value, key) => {
            data.push({ x: moment(key, "YYYY-MM-DD"), y: value });
            this.formatLabel(labels, key);
        });

        this.chartConfig.data.datasets = [{
            data,
            borderColor: 'rgba(157, 169, 83, 1)',
            fill: true,
            backgroundColor: 'rgba(0,124,190,0.1)',
            tension: 0.1,
            pointBackgroundColor: 'rgba(157, 169, 83, 1)',
            pointHoverBorderColor: 'rgba(157, 169, 83, 1)',
            pointHoverBorderWidth: 5
        }];
        this.chartConfig.data.labels = labels;
    }

    formatLabel(labels, value) {
        labels.push(moment(value, "YYYY-MM-DD").format("MMM DD"));
    }

    onHoverChart(event, elements, chart) {
        let isHovering = false;

        if (!isHovering) {
            this.previousRange = null;
            chart.update();
        }
    }

    tooltipExternalHandle(context) {
        const completedEvaluations = this.cvLocalizationService.getCachedText('Evaluator_Performance', 'Completed_Evaluations', 'Completed Evaluations');
        let tooltipEl = document.getElementById('chartjs-tooltip-trend');

        // tooltip content element creation
        if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip-trend';
            document.body.appendChild(tooltipEl);
        }

        // style element creation
        let styleEl = document.createElement('style');
        styleEl.type = 'text/css';
        var css = `
            .tooltip-content-EP-trend {
                display: flex; 
                align-items: end; 
                background-color: white; 
                position: relative; 
                border: 1px solid #D2D8DB; 
                width: fit-content; 
                height: 30px; 
                overflow: show; 
                box-shadow: 0 6px 12px rgba(0,0,0,.175);
            }

            .tooltip-content-EP-trend p {
                white-space: nowrap; 
                margin: 5px
            }

            .tooltip-content-EP-trend p::after {
                content: '';
                position: absolute;
                top: 100%;
                left: 50%;
                margin-left: -5px;
                border-width: 5px;
                border-style: solid;
                border-color: white transparent transparent transparent;
            }
        `;
        styleEl.appendChild(document.createTextNode(css));
        document.head.appendChild(styleEl);

        const tooltipModel = context.tooltip;
        if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = '0';
            return;
        }
        if (tooltipModel.body) {
            const dataFromCurrentElement = Math.round(tooltipModel.dataPoints[0].parsed.y);
            const innerHtml = `
                <div class="tooltip-content-EP-trend">
                    <p>${completedEvaluations}: <b>${dataFromCurrentElement}</b></p>
                </div>
            `;
            tooltipEl.innerHTML = innerHtml;
        }
        const position = context.chart.canvas.getBoundingClientRect();
        tooltipEl.style.opacity = '1';
        tooltipEl.style.position = 'absolute';
        tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY - tooltipEl.clientHeight + 'px';
        tooltipEl.style.left = (position.left + window.pageXOffset + tooltipModel.caretX) - 95 + 'px';
        tooltipEl.style.padding = '5px';
        tooltipEl.style.pointerEvents = 'none';
        tooltipEl.style.zIndex = '1000';
    }
}
